import { memo, useCallback, useMemo } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { ReassignmentItem } from 'clickNgo/features/contact/contactReassignment'
import { Nullable } from 'cng-common/models/utils'

import { ContactRelinkingStep } from '../../../configs'
import { useReassignment } from '../../../contexts/ReassignmentContext'
import { ContactRelinkingStepProps } from '../../../models'
import { contactRelinkingModalTranslations } from '../../../translation'
import { Reassignment } from '../../reassignment'

import { ReassignUniqueRolesDetails } from './components/reassignUniqueRolesDetails/ReassignUniqueRolesDetails'

export const ReassignUniqueRolesStep = memo<ContactRelinkingStepProps>(
  ({ onCancel, onProceed }) => {
    const {
      uniqueRolesToReassign,
      rolesToReassign,
      flagsToReassign,
      availableContacts,
      updateUniqueRoleAssignment,
      reassignmentUniqueRolesOption,
      setReassignmentUniqueRolesOption,
      targetsContactsWithRoleForRemoving,
      rolesForRemovingFromRelinkingContacts,
      isAskingHowRemoveUniqueRoles,
      commonUniqueRolesWithTarget,
    } = useReassignment()

    const targetPartnerContactsRoles = useMemo(
      () => targetsContactsWithRoleForRemoving.flatMap((el) => el.rolesForRemoving),
      [targetsContactsWithRoleForRemoving]
    )

    const items: ReassignmentItem[] = useMemo(
      () =>
        uniqueRolesToReassign.map((role) => ({
          key: role.role,
          displayName: role.role,
          contact: role.contact || null,
        })),
      [uniqueRolesToReassign]
    )

    const isDisableNextButton = useMemo(() => {
      if (!rolesForRemovingFromRelinkingContacts.length && !targetPartnerContactsRoles.length) {
        return false
      }
      return isAskingHowRemoveUniqueRoles && !reassignmentUniqueRolesOption
    }, [
      isAskingHowRemoveUniqueRoles,
      reassignmentUniqueRolesOption,
      rolesForRemovingFromRelinkingContacts.length,
      targetPartnerContactsRoles.length,
    ])

    const handleProceedClick = useCallback(() => {
      if (rolesToReassign.length > 0) {
        onProceed(ContactRelinkingStep.ReassignRoles)
        return
      }
      if (flagsToReassign.length > 0) {
        onProceed(ContactRelinkingStep.ReassignFlags)
        return
      } else {
        onProceed(ContactRelinkingStep.Completion)
      }
    }, [rolesToReassign.length, flagsToReassign.length, onProceed])

    const handleRoleChange = useCallback(
      (roleKey: string, contact: Nullable<IContact>) => {
        updateUniqueRoleAssignment(roleKey, contact)
      },
      [updateUniqueRoleAssignment]
    )

    return (
      <Reassignment
        title={contactRelinkingModalTranslations.reassignUniqueRoles.title}
        description={contactRelinkingModalTranslations.reassignUniqueRoles.description}
        warningMsg={
          availableContacts.length
            ? contactRelinkingModalTranslations.reassignUniqueRoles.warning
            : contactRelinkingModalTranslations.reassignUniqueRoles.notAvailableContacts
        }
        items={items}
        onItemChange={handleRoleChange}
        onCancel={onCancel}
        onProceed={handleProceedClick}
        isDisabled={isDisableNextButton}
        additionalContent={
          <ReassignUniqueRolesDetails
            reassignmentUniqueRolesOption={reassignmentUniqueRolesOption}
            isAskingHowRemoveUniqueRoles={isAskingHowRemoveUniqueRoles}
            rolesForRemovingFromRelinkingContacts={rolesForRemovingFromRelinkingContacts}
            setReassignmentUniqueRolesOption={setReassignmentUniqueRolesOption}
            targetPartnerContactsRoles={targetPartnerContactsRoles}
            commonUniqueRolesWithTarget={commonUniqueRolesWithTarget}
          />
        }
      />
    )
  }
)

ReassignUniqueRolesStep.displayName = 'ReassignRolesStep'
